import React from "react";
import type { TFunction } from "i18next";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";

type Props = {
  children: React.ReactNode;
  /** Used to offer “open in new tab” if blob URL not yet available from child */
  pdfData: ArrayBuffer;
  t: TFunction;
  onDismiss: () => void;
};

type State = { error: Error | null; fallbackBlobUrl: string | null };

/**
 * Catches errors from lazy-loaded PDF dialog (and anything below) so the app-level ErrorBoundary does not take over the whole screen.
 */
export class InAppPdfGateBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, fallbackBlobUrl: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error("[InAppPdfGateBoundary]", error.message, info.componentStack);
    try {
      const url = URL.createObjectURL(new Blob([this.props.pdfData], { type: "application/pdf" }));
      this.setState({ fallbackBlobUrl: url });
    } catch {
      /* ignore */
    }
  }

  override componentDidUpdate(prevProps: Props): void {
    if (prevProps.pdfData !== this.props.pdfData) {
      this.revokeFallback();
      this.setState({ error: null, fallbackBlobUrl: null });
    }
  }

  override componentWillUnmount(): void {
    this.revokeFallback();
  }

  private revokeFallback(): void {
    const u = this.state.fallbackBlobUrl;
    if (u) {
      try {
        URL.revokeObjectURL(u);
      } catch {
        /* ignore */
      }
    }
  }

  private handleClose = (): void => {
    this.revokeFallback();
    this.setState({ error: null, fallbackBlobUrl: null });
    this.props.onDismiss();
  };

  private handleRetry = (): void => {
    this.revokeFallback();
    this.setState({ error: null, fallbackBlobUrl: null });
  };

  override render(): React.ReactNode {
    const { children, t } = this.props;
    if (this.state.error) {
      const blobUrl = this.state.fallbackBlobUrl;
      return (
        <div
          className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-4 bg-background/95 p-4"
          role="alert"
        >
          <div className="flex max-w-md flex-col gap-3 rounded-lg border border-destructive/50 bg-card p-4 shadow-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden />
              <div>
                <p className="font-medium text-foreground">{t("manuals.pdfViewerLoadFailed")}</p>
                <p className="mt-1 text-xs text-muted-foreground">{this.state.error.message}</p>
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              {blobUrl ? (
                <Button variant="secondary" size="sm" asChild>
                  <a href={blobUrl} target="_blank" rel="noopener noreferrer">
                    {t("manuals.pdfOpenInNewTab")}
                  </a>
                </Button>
              ) : null}
              <Button type="button" variant="outline" size="sm" onClick={this.handleRetry}>
                {t("manuals.pdfViewerRetry")}
              </Button>
              <Button type="button" variant="default" size="sm" onClick={this.handleClose}>
                {t("manuals.pdfViewerClose")}
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return children;
  }
}
